import React from 'react';
import { ScrollView } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { useIsMobile } from '../../../../../hooks';
import { Stack } from '../../../../ui';

type ResponsiveProfileLayoutProps = {
  topSlot?: React.ReactNode;
  bannerSlot?: React.ReactNode;
  bottomSlot?: React.ReactNode;
  leftColumnSlot: React.ReactNode;
  rightColumnSlot: React.ReactNode;
  inNewDesktopBreakpoint: boolean;
  inNewMobileBreakpoint: boolean;
};

/**
 * This component is responsible for top level layout of the Profile page
 * The layout is as follows:
 * @param props {Object}
 * @param props.topSlot {ReactNode} - This slot is rendered at the top of the scroll view and
 *      takes up the full width of the screen, unconstrained by the max profile content width
 * @param props.bannerSlot {ReactNode} - This slot is rendered at the top of the scroll view
 *      within the width-constrained part of the profile. It takes the full width of the
 *      max width profile content
 * @param props.leftColumnSlot {ReactNode} - This slot represents either:
 *      1. The full main content of the profile page on mobile
 *      2. The left column content of the profile page on desktop
 * @param props.rightColumnSlot {ReactNode} - This slot is ONLY RENDERED ON DESKTOP and
 *      contains the right side column of the profile on desktop
 * @param props.bottomSlot {ReactNode} - This slot is rendered within the width-constrained part
 *      of the profile. It is shown below the column layout on desktop and below the
 *      leftColumnSlot on mobile.
 *
 *
 * Desktop layout:
 *  ________________________________________________________
 * |    |------------topSlot (max width 930) ----------|    |
 * |    |--------- bannerSlot (max width 930)----------|    |
 * |    |---- leftColumnSlot ----||-- rightColumnSlot--|    |
 * |    |--------------- bottomSlot -------------------|    |
 * |________________________________________________________|
 *
 * Mobile layout:
 *  _______________________
 * |------- topSlot -------|
 * |----- bannerSlot ------|
 * |--- leftColumnSlot ----|
 * |----- bottomSlot ------|
 * |_______________________|
 *
 */
const ResponsiveProfileLayout: React.FCWithChildren<ResponsiveProfileLayoutProps> = ({
  topSlot,
  bannerSlot,
  leftColumnSlot,
  rightColumnSlot,
  bottomSlot,
  inNewDesktopBreakpoint,
  inNewMobileBreakpoint,
}) => {
  const isMobile = useIsMobile();

  return (
    <ScrollView
      style={{
        maxWidth: inNewDesktopBreakpoint ? 798 : 930,
        width: inNewMobileBreakpoint ? '100%' : 'calc(100vw - 96px)',
      }}
      testID="Profile-content"
    >
      {topSlot}
      <Stack
        maxWidth="100%"
        alignSelf="center"
        testID="providerProfile"
        style={{
          width: '100%',
        }}
      >
        {bannerSlot}
        <Inline
          paddingTop={8}
        >
          {/* Mobile stack */}
          <Stack
            flexGrow={3}
            flexShrink={3}
            flexBasis={1}
            testID="proprofile__main"
          >
            {leftColumnSlot}
          </Stack>
          {(isMobile || inNewMobileBreakpoint) ? null : (
            <Box
              paddingLeft={20}
              flexGrow={2}
              flexShrink={2}
              flexBasis={1}
            >
              {rightColumnSlot}
            </Box>
          )}
        </Inline>
        {bottomSlot}
      </Stack>
    </ScrollView>
  );
};

export default ResponsiveProfileLayout;
